// /changelog — hand-seeded from recent git. Grouped by shippable
// milestone, not every commit. When we ship enough that this gets
// long, split into a repo-generated file or a connector.
import { useSeo } from "@/hooks";
import { MarketingShell } from "./MarketingShell";

type Entry = { date: string; version?: string; title: string; items: string[] };

const ENTRIES: Entry[] = [
  {
    date: "2026-10-24",
    version: "0.4",
    title: "Landing + marketing surface",
    items: [
      "New landing page at / with recent catches, stats, capabilities, how-it-works, FAQ.",
      "Docs, changelog, status, pricing, security, privacy, and terms pages.",
      "Submit flow moved to /submit.",
    ],
  },
  {
    date: "2026-10-20",
    version: "0.3",
    title: "Parallel runs + HTTPS body view",
    items: [
      "Nixbox runs two Windows VMs in parallel. Per-job UUIDs so concurrent dispatch doesn't collide.",
      "Decrypted HTTP request + response bodies embedded in net.http events — the Network tab's HTTP sub-tab now has a 'view content' chip opening a hex-or-text dialog.",
      "Scheduler holds the slot until QEMU teardown fully reaps + overcommit_memory=1 + zram on nixbox — eliminates the 'cannot allocate memory' race under archive-expansion bursts.",
      "W32Time resync task baked into the Windows image so TLS certs validate on first HTTPS from a cold boot.",
    ],
  },
  {
    date: "2026-10-19",
    version: "0.2",
    title: "Archive expansion + bulk submissions",
    items: [
      "Submit a zip, 7z, or rar (optionally password-protected). Backend extracts server-side and queues one job per Windows runnable inside — .exe / .dll / .ps1 / .bat / .vbs / .hta / .lnk / .msi, et al. ELF / Mach-O skipped.",
      "Common-password list ('', 'infected', 'malware', 'virus', 'password') tried automatically.",
      "Multipart presigned-S3 uploads bypass Cloudflare's 100 MB request-body cap. Big MalwareBazaar hourly dumps work.",
      "Sample dedupe: re-submission of a sha already in storage skips the upload entirely.",
      "Rerun button on every job's report.",
    ],
  },
  {
    date: "2026-10-19",
    version: "0.1",
    title: "Suricata + hex view + config extraction",
    items: [
      "Suricata NIDS worker reads every pcap. Alerts land as suricata_alert events and render as severity-tinted badges on Network tab rows.",
      "Per-flow hex view: click 'bytes' on a Connections / Flows row to see the TCP-reassembled bytes for that flow.",
      "Off-host config-extractor pipeline: Rust registry (40+ RAT / stealer families) + MACO community parsers. Full extractor dump viewable per finding plus raw JSON.",
      "Verdict + Config figure now merge all sources instead of letting the stale report shadow the live events.",
    ],
  },
];

function Pill({ children }: { children: React.ReactNode }) {
  return <span className="inline-flex items-center border border-rule-row bg-paper px-2 py-0.5 font-mono text-meta tabular-nums text-ink-soft">{children}</span>;
}

export function ChangelogPage() {
  useSeo({
    title: "Changelog — malbox malware sandbox",
    description: "What shipped in malbox, grouped by release. Decrypted HTTPS, hex view, Suricata NIDS, config extractors, parallel runs, and more.",
    path: "/changelog",
    keywords: "malbox changelog, malware sandbox updates, malware sandbox releases",
  });
  return (
    <MarketingShell
      eyebrow="Changelog"
      title="Shipped."
      lede={<>Grouped by milestone, newest first. Each release lands on <span className="font-mono">malbox.neels.dev</span> the day it ships.</>}
    >
      <ol className="space-y-10">
        {ENTRIES.map((e) => (
          <li key={e.date + e.title} className="border-t border-rule-row pt-6">
            <div className="flex flex-wrap items-baseline gap-3">
              {e.version && <Pill>v{e.version}</Pill>}
              <time className="meta tabular-nums">{e.date}</time>
            </div>
            <h2
              className="mt-3 font-mono text-ink"
              style={{ fontSize: "clamp(20px, 2vw, 26px)", letterSpacing: "-0.02em" }}
            >
              {e.title}
            </h2>
            <ul className="mt-3 max-w-[760px] list-inside list-disc space-y-2 pl-2 text-ui text-ink-soft">
              {e.items.map((i) => <li key={i}>{i}</li>)}
            </ul>
          </li>
        ))}
      </ol>
    </MarketingShell>
  );
}
